import type { ViewKey } from '@/domain';

/**
 * The route table from section 5, as the view each path belongs to.
 *
 * The route guard needs to answer "which screen is this?" for an arbitrary
 * pathname, which React Router's own matching does not expose from a layout
 * route. Ordered longest first so `/line/:stage` is the stage detail rather
 * than the production line.
 *
 * `/print/:kitId` is deliberately absent. Section 5 grants the printed job
 * sheet to "anyone who can open the kit", and that is four different screens:
 * the queue, the production line, orders and the archive. Mapping it to any one
 * of them would stop the others printing, which is what happened when it was
 * mapped to `job` and Management, who hold no queue, could not print at all.
 * A path with no entry here is not guarded, and the server still decides what
 * the payload contains.
 */
export const ROUTE_VIEWS: ReadonlyArray<{ pattern: RegExp; view: ViewKey }> = [
  /*
   * Logistics is NOT a stage, so it is not behind the stage view and it is
   * matched before the stage pattern, which this table checks in order.
   * Section 2 of the request of 13 September 2026.
   */
  { pattern: /^\/line\/logistics$/, view: 'line' },
  { pattern: /^\/line\/[^/]+$/, view: 'stage' },
  { pattern: /^\/queue\/[^/]+$/, view: 'job' },
  { pattern: /^\/archive\/[^/]+$/, view: 'record' },
  { pattern: /^\/orders\/[^/]+$/, view: 'orders' },
  // The full record of a live order, the same screen the archive uses. Held
  // by `orders` because it is reached from the orders list and shows nothing
  // the order drawer does not already entitle the reader to.
  { pattern: /^\/orders\/[^/]+\/record$/, view: 'orders' },
  // Editing a case after intake, reached from the order drawer and the job
  // view. The screen itself also requires editCase. Handover 2.4.
  { pattern: /^\/orders\/[^/]+\/edit$/, view: 'orders' },
  { pattern: /^\/qc\/[^/]+$/, view: 'finalcheck' },
  // The work record of one person. Reached from the team list and guarded by
  // the same `users` grant, because it is the same screen from a manager's
  // point of view and it carries no figure the team list does not.
  { pattern: /^\/users\/[^/]+$/, view: 'users' },
  // A doctor's and a patient's profile, each held by its list: whoever may
  // read the list may open a record on it. The request of 6 October 2026
  // (second), item 4.
  { pattern: /^\/doctors\/[^/]+$/, view: 'doctors' },
  { pattern: /^\/patients\/[^/]+$/, view: 'patients' },
  // The payment summary of a case and a doctor's statement, both Payments' own
  // pages: whoever takes payments reads them. The request of 8 October 2026 (fourth).
  { pattern: /^\/payments\/case\/[^/]+$/, view: 'payments' },
  { pattern: /^\/payments\/doctor\/[^/]+$/, view: 'payments' },

  { pattern: /^\/$/, view: 'overview' },
  // A person's own page of widgets. The lab's request of 12 October 2026.
  { pattern: /^\/dashboard$/, view: 'dashboard' },
  { pattern: /^\/line$/, view: 'line' },
  { pattern: /^\/orders$/, view: 'orders' },
  { pattern: /^\/intake$/, view: 'intake' },
  { pattern: /^\/queue$/, view: 'queue' },
  { pattern: /^\/scan$/, view: 'scan' },
  { pattern: /^\/qc$/, view: 'finalcheck' },
  { pattern: /^\/payments$/, view: 'payments' },
  { pattern: /^\/on-hold$/, view: 'onhold' },
  { pattern: /^\/doctors$/, view: 'doctors' },
  { pattern: /^\/patients$/, view: 'patients' },
  { pattern: /^\/shipping$/, view: 'shipping' },
  { pattern: /^\/archive$/, view: 'archive' },
  { pattern: /^\/reports$/, view: 'reports' },
  { pattern: /^\/staff$/, view: 'staff' },
  { pattern: /^\/activity$/, view: 'activity' },
  { pattern: /^\/users$/, view: 'users' },
  { pattern: /^\/settings$/, view: 'settings' },
  { pattern: /^\/items$/, view: 'items' },
  { pattern: /^\/perf$/, view: 'perf' },
];
